import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Link,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  Textarea,
  Title3,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  Attach16Regular,
  CheckmarkCircle24Regular,
  DismissCircle24Regular,
} from "@fluentui/react-icons";
import { useApp } from "../context/AppContext";
import { Holiday, LeaveRequest } from "../models/leaveModels";
import { Decision } from "../services/leaveService";
import { URGENT_WITHIN_WORKING_DAYS } from "../config/leavePolicy";
import { formatDateList, todayISO } from "../utils/dateUtils";
import { workingDaysUntil } from "../utils/workingDays";
import { StatusBadge } from "./shared/StatusBadge";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalL },
  empty: {
    padding: tokens.spacingVerticalXXL,
    textAlign: "center",
    color: tokens.colorNeutralForeground3,
  },
  meta: { color: tokens.colorNeutralForeground3 },
});

interface DecisionTarget {
  request: LeaveRequest;
  decision: Decision;
}

/** How soon a pending request starts, colored by urgency. */
function startsIn(
  r: LeaveRequest,
  holidays: Holiday[],
  today: string,
): { label: string; color: "danger" | "warning" | "informative" } {
  if (r.startDate < today) return { label: "Already started", color: "danger" };
  if (r.startDate === today) return { label: "Today", color: "danger" };
  const n = workingDaysUntil(today, r.startDate, holidays);
  return {
    label: `In ${n} working day${n === 1 ? "" : "s"}`,
    color: n <= 1 ? "danger" : n <= URGENT_WITHIN_WORKING_DAYS ? "warning" : "informative",
  };
}

function AttachmentLink({ request }: { request: LeaveRequest }) {
  if (!request.attachmentUrl) return null;
  return (
    <Link
      href={request.attachmentUrl}
      target="_blank"
      rel="noopener noreferrer"
      style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
    >
      <Attach16Regular />
      {request.leaveTypeCode === "SL" ? "Medical certificate" : "Attachment"}
    </Link>
  );
}

function daysLabel(r: LeaveRequest): string {
  return r.lwopDays > 0 ? `${r.workingDays} (${r.lwopDays} LWOP)` : String(r.workingDays);
}

export function ManagerQueue() {
  const s = useStyles();
  const { service, currentUser, holidays, refreshSignal, triggerRefresh, dataMode } =
    useApp();
  const today = todayISO();

  const [pending, setPending] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState<DecisionTarget | null>(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    service.getPendingForManager(currentUser.email).then((rows) => {
      if (cancelled) return;
      // Soonest leave first — those are the ones that need a decision now.
      setPending([...rows].sort((a, b) => a.startDate.localeCompare(b.startDate)));
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [service, currentUser.email, refreshSignal]);

  function openDecision(request: LeaveRequest, decision: Decision) {
    setTarget({ request, decision });
    setNotes(decision === "Approved" ? "Approved." : "");
  }

  async function submitDecision() {
    if (!target?.request.id) return;
    setBusy(true);
    try {
      await service.decideLeaveRequest(
        target.request.id,
        target.decision,
        currentUser.email,
        notes,
      );
      setTarget(null);
      triggerRefresh();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={s.root}>
      <Title3>Pending approvals</Title3>

      {loading ? (
        <Spinner label="Loading your queue…" />
      ) : pending.length === 0 ? (
        <div className={s.empty}>
          🎉 Nothing waiting on you. Requests from your reports show up here.
        </div>
      ) : (
        <Table aria-label="Pending leave requests" size="medium">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Employee</TableHeaderCell>
              <TableHeaderCell>Type</TableHeaderCell>
              <TableHeaderCell>Dates</TableHeaderCell>
              <TableHeaderCell>Days</TableHeaderCell>
              <TableHeaderCell>Reason</TableHeaderCell>
              <TableHeaderCell>Starts</TableHeaderCell>
              <TableHeaderCell>Decision</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pending.map((r) => {
              const starts = startsIn(r, holidays, today);
              return (
                <TableRow key={r.id}>
                  <TableCell title={`${r.employee}\n${r.employeeEmail}`}>
                    <TableCellLayout
                      truncate
                      description={r.employee !== r.employeeEmail ? r.employeeEmail : undefined}
                    >
                      {r.employee}
                    </TableCellLayout>
                  </TableCell>
                  <TableCell>{r.leaveTypeCode}</TableCell>
                  <TableCell>{formatDateList(r.leaveDates)}</TableCell>
                  <TableCell>{daysLabel(r)}</TableCell>
                  <TableCell>
                    <Text truncate wrap={false} style={{ maxWidth: 220, display: "block" }}>
                      {r.reason}
                    </Text>
                    <AttachmentLink request={r} />
                  </TableCell>
                  <TableCell>
                    <Badge appearance="tint" color={starts.color}>
                      {starts.label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      appearance="primary"
                      icon={<CheckmarkCircle24Regular />}
                      onClick={() => openDecision(r, "Approved")}
                    >
                      Approve
                    </Button>{" "}
                    <Button
                      size="small"
                      icon={<DismissCircle24Regular />}
                      onClick={() => openDecision(r, "Rejected")}
                    >
                      Reject
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {dataMode === "graph" && pending.length > 0 && (
        <Text className={s.meta} size={200}>
          Deciding here works the same as the Teams card: Power Automate picks up
          the new status, updates the balance, and notifies the employee.
        </Text>
      )}

      <Dialog open={!!target} onOpenChange={(_, d) => !d.open && setTarget(null)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {target?.decision === "Approved" ? "Approve" : "Reject"} leave request
            </DialogTitle>
            <DialogContent>
              {target && (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <StatusBadge status={target.request.status} />
                    <Text>
                      {target.request.employee} · {target.request.leaveTypeCode} ·{" "}
                      {daysLabel(target.request)} day(s)
                    </Text>
                  </div>
                  <Text size={200} className={s.meta}>
                    {formatDateList(target.request.leaveDates)}
                  </Text>
                  <AttachmentLink request={target.request} />
                  <Textarea
                    placeholder="Decision notes (optional)"
                    value={notes}
                    onChange={(_, d) => setNotes(d.value)}
                  />
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setTarget(null)}>
                Back
              </Button>
              <Button appearance="primary" disabled={busy} onClick={submitDecision}>
                {busy
                  ? "Saving…"
                  : target?.decision === "Approved"
                    ? "Confirm approval"
                    : "Confirm rejection"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
